import { createActionHandler, createReducer, OperationStatus } from '@pressreader/appstore';
import { userChangeDone } from '@pressreader/authentication-core';
import type { ArticleCollections, IArticleSharePreview, ShareProvider } from '@pressreader/social-types';

import {
    loadBookmarksForArticleBegan,
    loadBookmarksForArticleDone,
    loadBookmarksForArticleFailed,
    loadShareInfoForArticleDone,
    resetBookmarksAction,
} from '../actions/share';

interface State {
    bookmarks: Record<string, { status: OperationStatus; bookmarks: ArticleCollections[] }>;
    providers: {
        [articleId: string]: { status: OperationStatus; supported: ShareProvider[] };
    };
    previews: {
        [articleId: string]: IArticleSharePreview;
    };
}

const initialState: State = {
    bookmarks: {},
    providers: {},
    previews: {},
};

function getArticleBookmarksState(state: State, articleId: string) {
    if (articleId in state.bookmarks) {
        return state.bookmarks[articleId];
    }

    return { status: OperationStatus.None, bookmarks: [] };
}

const reducer = createReducer(
    initialState,
    createActionHandler(loadBookmarksForArticleBegan, (state, { payload }) => {
        const articleBookmarksState = {
            ...getArticleBookmarksState(state, payload.articleId),
            status: OperationStatus.Pending,
        };

        return {
            ...state,
            bookmarks: {
                ...state.bookmarks,
                [payload.articleId]: articleBookmarksState,
            },
        };
    }),
    createActionHandler(loadBookmarksForArticleDone, (state, { payload }) => {
        const articleBookmarksState = {
            ...getArticleBookmarksState(state, payload.articleId),
            status: OperationStatus.Done,
            bookmarks: payload.bookmarks,
        };

        return {
            ...state,
            bookmarks: {
                ...state.bookmarks,
                [payload.articleId]: articleBookmarksState,
            },
        };
    }),
    createActionHandler(loadBookmarksForArticleFailed, (state, { payload }) => {
        const articleBookmarksState = {
            ...getArticleBookmarksState(state, payload.articleId),
            status: OperationStatus.Failed,
        };

        return {
            ...state,
            bookmarks: {
                ...state.bookmarks,
                [payload.articleId]: articleBookmarksState,
            },
        };
    }),
    createActionHandler(userChangeDone, state => ({
        ...state,
        previews: {},
        providers: {},
    })),
    createActionHandler(loadShareInfoForArticleDone, (state, { payload }) => {
        const { articleId } = payload;
        return {
            ...state,
            previews: {
                ...state.previews,
                [articleId]: payload.preview,
            },
            providers: {
                ...state.providers,
                [articleId]: {
                    supported: payload.supported,
                    status: OperationStatus.Done,
                },
            },
        };
    }),
    createActionHandler(resetBookmarksAction, state => ({
        ...state,
        bookmarks: {},
    })),
);

export { State, reducer };
